import type { ServiceSummary } from "@veo/types";
import { fetchApi } from "./api";
import type { PostSummary } from "../components/ArticleCard";
import type { CmsPage } from "./page";

/**
 * Data that CMS blocks need from other parts of the API.
 *
 * Five block types show content they do not own — services, articles, FAQs,
 * testimonials and team members. Fetching that from inside the component would
 * mean it is missing from the server-rendered HTML, which on a marketing site
 * is the difference between a search engine seeing four named services and
 * seeing an empty div.
 *
 * So it is fetched in the route loader, keyed by section id, and handed to each
 * block as a prop. Identical requests are made once: three article blocks with
 * the same settings cost one round trip.
 */

export interface Faq {
  id: string;
  question: string;
  answer: { type: string; content?: unknown[] } | null;
  categoryLabel: string | null;
}

export interface Testimonial {
  id: string;
  quote: string;
  authorName: string;
  authorTitle: string | null;
  authorCompany: string | null;
  rating: number | null;
}

export interface TeamMember {
  id: string;
  slug: string;
  name: string;
  role: string;
  bio: { type: string; content?: unknown[] } | null;
  credentials: string[];
  photoUrl: string | null;
  linkedinUrl: string | null;
}

export type SectionData = Record<string, unknown>;

/** The endpoint a section needs, or null when it is self-contained. */
function endpointFor(section: CmsPage["sections"][number]): string | null {
  const content = section.content as Record<string, unknown>;

  switch (section.sectionType) {
    case "servicesGrid":
      return "/public/services";

    case "articleListing": {
      const params = new URLSearchParams({ perPage: String(content.limit ?? 3) });
      if (content.source === "category" && typeof content.categoryId === "string") {
        params.set("category", content.categoryId);
      }
      return `/public/posts?${params.toString()}`;
    }

    case "faqAccordion":
      return typeof content.serviceId === "string" && content.serviceId
        ? `/public/faqs?service=${encodeURIComponent(content.serviceId)}`
        : "/public/faqs";

    case "testimonials":
      return "/public/testimonials?limit=12";

    case "team":
      return "/public/team";

    default:
      return null;
  }
}

export async function loadSectionData(sections: CmsPage["sections"]): Promise<SectionData> {
  const byEndpoint = new Map<string, Promise<unknown>>();
  const sectionEndpoints: [string, string][] = [];

  for (const section of sections) {
    const endpoint = endpointFor(section);
    if (!endpoint) continue;

    sectionEndpoints.push([section.id, endpoint]);

    if (!byEndpoint.has(endpoint)) {
      /**
       * A failure here degrades one block, not the page. A testimonials
       * endpoint having a bad day should not take the homepage down with it.
       */
      byEndpoint.set(
        endpoint,
        fetchApi<unknown>(endpoint).catch(() => null),
      );
    }
  }

  const resolved = new Map(
    await Promise.all(
      [...byEndpoint].map(
        async ([endpoint, promise]) => [endpoint, await promise] as [string, unknown],
      ),
    ),
  );

  return Object.fromEntries(
    sectionEndpoints.map(([sectionId, endpoint]) => [sectionId, resolved.get(endpoint) ?? null]),
  );
}

/** Narrowing helpers, so a block never has to trust an `unknown` shape. */
export const asServices = (data: unknown): ServiceSummary[] | null =>
  Array.isArray(data) ? (data as ServiceSummary[]) : null;

export const asPosts = (data: unknown): PostSummary[] | null => {
  const record = data as { data?: unknown } | null;
  return Array.isArray(record?.data) ? (record.data as PostSummary[]) : null;
};

export const asFaqs = (data: unknown): Faq[] | null =>
  Array.isArray(data) ? (data as Faq[]) : null;

export const asTestimonials = (data: unknown): Testimonial[] | null =>
  Array.isArray(data) ? (data as Testimonial[]) : null;

export const asTeam = (data: unknown): TeamMember[] | null =>
  Array.isArray(data) ? (data as TeamMember[]) : null;
